import { useState, useMemo } from 'react'
import { DENOMINATION_OPTIONS } from '@fellowship/shared'
import {
  useAdminScrapedChurches,
  useAdminScrapedEvents,
  useAdminUpdateChurch,
  useAdminDeleteChurch,
  useAdminDeleteEvent,
  useAdminChurchOutreach,
} from '../../hooks/useAdmin'
import type { AdminChurch, AdminEvent } from '../../types/admin'

// ─── Inline church editor ─────────────────────────────────────────────────────

function ChurchEditForm({ church, onClose }: { church: AdminChurch; onClose: () => void }) {
  const update = useAdminUpdateChurch()
  const [fields, setFields] = useState({
    name: church.name ?? '',
    denomination: church.denomination ?? '',
    addressLine1: church.addressLine1 ?? '',
    city: church.city ?? '',
    state: church.state ?? '',
    phone: church.phone ?? '',
    email: church.email ?? '',
    websiteUrl: church.websiteUrl ?? '',
  })

  function set(key: keyof typeof fields) {
    return (e: React.ChangeEvent<HTMLInputElement>) => setFields((prev) => ({ ...prev, [key]: e.target.value }))
  }

  function handleSave() {
    update.mutate(
      {
        id: church.id,
        name: fields.name || undefined,
        denomination: fields.denomination || undefined,
        address_line1: fields.addressLine1 || undefined,
        city: fields.city || undefined,
        state: fields.state || undefined,
        phone: fields.phone || null,
        email: fields.email || null,
        website_url: fields.websiteUrl || null,
      },
      { onSuccess: onClose },
    )
  }

  const row: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 }
  const label: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }
  const input: React.CSSProperties = {
    padding: '7px 10px',
    borderRadius: 6,
    border: '1px solid var(--line)',
    background: 'var(--bg)',
    fontSize: 13,
    fontFamily: 'var(--font-sans)',
    color: 'var(--ink)',
    outline: 'none',
  }

  return (
    <div style={{ marginTop: 16, background: 'var(--surface-2)', borderRadius: 10, padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Edit church data</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={row}><span style={label}>Name</span><input style={input} value={fields.name} onChange={set('name')} /></div>
        <div style={row}>
          <span style={label}>Denomination</span>
          <select style={input} value={fields.denomination} onChange={(e) => setFields((f) => ({ ...f, denomination: e.target.value }))}>
            <option value="">Unknown</option>
            {DENOMINATION_OPTIONS.map((d) => (
              <option key={d.key} value={d.key}>{d.label}</option>
            ))}
            {fields.denomination && !DENOMINATION_OPTIONS.some((d) => d.key === fields.denomination) && (
              <option value={fields.denomination}>{fields.denomination}</option>
            )}
          </select>
        </div>
        <div style={{ ...row, gridColumn: '1 / -1' }}><span style={label}>Address</span><input style={input} value={fields.addressLine1} onChange={set('addressLine1')} placeholder="123 Main St" /></div>
        <div style={row}><span style={label}>City</span><input style={input} value={fields.city} onChange={set('city')} /></div>
        <div style={row}><span style={label}>State</span><input style={input} value={fields.state} onChange={set('state')} /></div>
        <div style={row}><span style={label}>Phone</span><input style={input} value={fields.phone} onChange={set('phone')} placeholder="+1 (555) 000-0000" /></div>
        <div style={row}><span style={label}>Email</span><input style={input} value={fields.email} onChange={set('email')} placeholder="pastor@church.org" type="email" /></div>
        <div style={{ ...row, gridColumn: '1 / -1' }}><span style={label}>Website</span><input style={input} value={fields.websiteUrl} onChange={set('websiteUrl')} placeholder="https://..." /></div>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid var(--line)', background: 'var(--surface)', fontSize: 13, fontFamily: 'var(--font-sans)', cursor: 'pointer', color: 'var(--ink-2)' }}>Cancel</button>
        <button
          onClick={handleSave}
          disabled={update.isPending}
          style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: 'linear-gradient(135deg, #3B4CCA, #7C5CD3)', color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-sans)', cursor: update.isPending ? 'not-allowed' : 'pointer', opacity: update.isPending ? 0.7 : 1 }}
        >
          {update.isPending ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  )
}

// ─── Queue item model ─────────────────────────────────────────────────────────

type QueueItem = {
  id: string
  kind: 'church' | 'event'
  title: string
  location: string
  source: string | null
  confidence: number
  age: string
  risk: string | null
  isPossibleDupe: boolean
  raw: AdminChurch | AdminEvent
}

function formatAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const hours = Math.floor(ms / 3_600_000)
  if (hours < 1) return '<1h'
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  return `${days}d`
}

function churchConfidence(c: AdminChurch): number {
  return (
    (c.name ? 1 : 0) +
    (c.addressLine1 ? 1 : 0) +
    (c.phone ? 1 : 0) +
    (c.email ? 1 : 0) +
    (c.websiteUrl ? 1 : 0)
  )
}

function eventConfidence(e: AdminEvent): number {
  const inFuture = new Date(e.createdAt).getTime() < Date.now()
  return (
    (e.title ? 1 : 0) +
    (e.description ? 1 : 0) +
    (e.addressText ? 1 : 0) +
    (inFuture ? 1 : 0) +
    (e.eventType !== 'potluck' ? 1 : 0)
  )
}

function churchRisk(c: AdminChurch): string | null {
  if (!c.addressLine1 && !c.city) return 'No address found'
  if (!c.phone && !c.email) return 'No contact info'
  return null
}

function eventRisk(e: AdminEvent): string | null {
  if (e.status === 'completed') return 'Past event detected'
  return null
}

// ─── Possible dupe detection ──────────────────────────────────────────────────

const COMMON_WORDS = new Set([
  'church', 'fellowship', 'community', 'christian', 'baptist', 'methodist',
  'adventist', 'sda', 'assembly', 'chapel', 'ministry', 'ministries', 'of',
  'the', 'and', 'at', 'first', 'new', 'grace', 'life', 'living', 'faith',
])

function normalizeName(name: string): string[] {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .split(/\s+/)
    .filter((w) => w.length > 2 && !COMMON_WORDS.has(w))
}

function computeDupeIds(churches: AdminChurch[]): Set<string> {
  const dupeIds = new Set<string>()
  for (let i = 0; i < churches.length; i++) {
    for (let j = i + 1; j < churches.length; j++) {
      const wordsA = normalizeName(churches[i].name)
      const wordsB = new Set(normalizeName(churches[j].name))
      const overlap = wordsA.filter((w) => wordsB.has(w))
      if (overlap.length >= 2 || (wordsA.length === 1 && wordsB.has(wordsA[0]))) {
        dupeIds.add(churches[i].id)
        dupeIds.add(churches[j].id)
      }
    }
  }
  return dupeIds
}

function toQueueItems(churches: AdminChurch[], events: AdminEvent[]): QueueItem[] {
  const dupeIds = computeDupeIds(churches)
  const cItems: QueueItem[] = churches.map((c) => ({
    id: c.id,
    kind: 'church',
    title: c.name,
    location: [c.city, c.state].filter(Boolean).join(', ') || 'Unknown location',
    source: c.sourceUrl ?? c.websiteUrl,
    confidence: churchConfidence(c),
    age: formatAge(c.createdAt),
    risk: churchRisk(c),
    isPossibleDupe: dupeIds.has(c.id),
    raw: c,
  }))
  const eItems: QueueItem[] = events.map((e) => ({
    id: e.id,
    kind: 'event',
    title: e.title,
    location: e.addressText || 'Unknown location',
    source: null,
    confidence: eventConfidence(e),
    age: formatAge(e.createdAt),
    risk: eventRisk(e),
    isPossibleDupe: false,
    raw: e,
  }))
  return [...cItems, ...eItems].sort((a, b) => a.confidence - b.confidence)
}

// ─── Confidence bars ──────────────────────────────────────────────────────────

function ConfBars({ score }: { score: number }) {
  const color = score >= 4 ? '#22c55e' : score >= 3 ? '#f59e0b' : '#ef4444'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <div style={{ display: 'flex', gap: 2 }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            style={{
              width: 5,
              height: 14,
              borderRadius: 1.5,
              background: i < score ? color : 'var(--line-soft)',
            }}
          />
        ))}
      </div>
      <span
        style={{ fontSize: 11.5, color: 'var(--muted)', fontFamily: 'ui-monospace,"SF Mono",Menlo,monospace' }}
      >
        {score}/5
      </span>
    </div>
  )
}

// ─── Detail panel ─────────────────────────────────────────────────────────────

function DetailPanel({
  item,
  onApprove,
  onDismiss,
  onOutreach,
  approving,
  dismissing,
  outreaching,
  outreachSent,
}: {
  item: QueueItem
  onApprove: () => void
  onDismiss: () => void
  onOutreach: () => void
  approving: boolean
  dismissing: boolean
  outreaching: boolean
  outreachSent: boolean
}) {
  const [editing, setEditing] = useState(false)
  const c = item.kind === 'church' ? (item.raw as AdminChurch) : null
  const e = item.kind === 'event' ? (item.raw as AdminEvent) : null

  const extractedJson = c
    ? JSON.stringify(
        {
          name: c.name,
          denomination: c.denomination || null,
          address: c.addressLine1 || null,
          city: c.city || null,
          state: c.state || null,
          phone: c.phone || null,
          email: c.email || null,
          website: c.websiteUrl || null,
          sourceUrl: c.sourceUrl || null,
        },
        null,
        2,
      )
    : JSON.stringify(
        {
          title: e?.title,
          eventType: e?.eventType,
          address: e?.addressText || null,
          description: e?.description || null,
          status: e?.status,
          visibility: e?.visibility,
          source: e?.source,
        },
        null,
        2,
      )

  return (
    <div style={{ marginTop: 24 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--muted)',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          marginBottom: 10,
        }}
      >
        Selected · {item.title}
      </div>

      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--line-soft)',
          borderRadius: 12,
          padding: 22,
          display: 'grid',
          gridTemplateColumns: '1.4fr 1fr 1fr',
          gap: 24,
        }}
      >
        {/* Left: info */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <span
              style={{
                fontSize: 10.5,
                padding: '3px 8px',
                borderRadius: 5,
                background: item.kind === 'church' ? '#EEF0FF' : '#F8E6EE',
                color: item.kind === 'church' ? '#3B4CCA' : '#C75D8A',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              {item.kind}
            </span>
            {item.risk && (
              <span
                style={{
                  fontSize: 10.5,
                  padding: '3px 8px',
                  borderRadius: 5,
                  background: '#fef3c7',
                  color: '#b45309',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                ⚠ {item.risk}
              </span>
            )}
            {item.isPossibleDupe && (
              <span
                style={{
                  fontSize: 10.5,
                  padding: '3px 8px',
                  borderRadius: 5,
                  background: '#EEF0FF',
                  color: '#3B4CCA',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                ⚠ Possible duplicate
              </span>
            )}
          </div>

          <h3
            style={{
              fontSize: 20,
              fontWeight: 600,
              letterSpacing: '-0.01em',
              margin: '0 0 4px',
              color: 'var(--ink)',
            }}
          >
            {item.title}
          </h3>
          <div style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>
            {item.source
              ? `${item.source} · scraped ${item.age} ago`
              : `Scraped ${item.age} ago`}
          </div>

          {item.kind === 'event' && e?.description && (
            <p
              style={{
                fontSize: 13,
                color: 'var(--ink-2)',
                marginTop: 12,
                lineHeight: 1.6,
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {e.description}
            </p>
          )}

          {item.risk && (
            <div
              style={{
                marginTop: 14,
                padding: '12px 14px',
                background: '#fef3c7',
                border: '1px solid #fde68a',
                borderRadius: 8,
                fontSize: 12.5,
                color: '#92400e',
                lineHeight: 1.5,
              }}
            >
              <strong>⚠ Note:</strong> {item.risk}
            </div>
          )}
        </div>

        {/* Middle: extracted data */}
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--muted)',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              marginBottom: 10,
            }}
          >
            Extracted data
          </div>
          <div
            style={{
              background: 'var(--surface-2)',
              borderRadius: 8,
              padding: '12px 14px',
              fontSize: 12,
              fontFamily: 'ui-monospace,"SF Mono",Menlo,monospace',
              lineHeight: 1.65,
              color: 'var(--ink)',
              whiteSpace: 'pre',
              overflowX: 'auto',
            }}
          >
            {extractedJson}
          </div>
          <div
            style={{
              fontSize: 11,
              color: 'var(--muted)',
              marginTop: 8,
              fontFamily: 'ui-monospace,"SF Mono",Menlo,monospace',
            }}
          >
            confidence {item.confidence}/5
          </div>
        </div>

        {/* Right: actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--muted)',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              marginBottom: 4,
            }}
          >
            Actions
          </div>
          <button
            onClick={onApprove}
            disabled={approving || item.kind === 'event'}
            style={{
              padding: '11px',
              borderRadius: 8,
              border: 'none',
              background: 'linear-gradient(135deg, #3B4CCA, #7C5CD3)',
              color: '#fff',
              fontSize: 13.5,
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              cursor: approving ? 'not-allowed' : 'pointer',
              opacity: approving ? 0.6 : 1,
              boxShadow: '0 4px 12px rgba(59,76,202,.35)',
            }}
          >
            {approving ? 'Verifying…' : item.kind === 'church' ? '✓ Verify church data' : 'Verify via church'}
          </button>
          {c && (
            <button
              onClick={() => setEditing((v) => !v)}
              style={{
                padding: '11px',
                borderRadius: 8,
                border: '1px solid var(--line)',
                background: editing ? 'var(--surface-2)' : 'var(--surface)',
                fontSize: 13.5,
                fontFamily: 'var(--font-sans)',
                fontWeight: 600,
                cursor: 'pointer',
                color: 'var(--ink)',
              }}
            >
              {editing ? '✕ Cancel edit' : '✎ Edit church data'}
            </button>
          )}
          {item.kind === 'church' && (
            <button
              style={{
                padding: '11px',
                borderRadius: 8,
                border: '1px solid var(--line)',
                background: 'var(--surface)',
                fontSize: 13.5,
                fontFamily: 'var(--font-sans)',
                fontWeight: 600,
                cursor: 'not-allowed',
                opacity: 0.5,
              }}
              title="Coming soon"
              disabled
            >
              Merge into existing church
            </button>
          )}
          <div style={{ height: 1, background: 'var(--line)', margin: '4px 0' }} />
          {item.kind === 'church' && (() => {
            const hasEmail = !!(item.raw as AdminChurch).email
            if (!hasEmail) {
              return (
                <button
                  disabled
                  title="No email on file for this church"
                  style={{
                    padding: '11px',
                    borderRadius: 8,
                    border: '1px solid var(--line)',
                    background: 'var(--surface)',
                    fontSize: 13.5,
                    fontFamily: 'var(--font-sans)',
                    fontWeight: 600,
                    color: 'var(--muted)',
                    cursor: 'not-allowed',
                    opacity: 0.45,
                  }}
                >
                  📨 Email outreach — no email on file
                </button>
              )
            }
            if (outreachSent) {
              return (
                <div style={{
                  padding: '11px',
                  borderRadius: 8,
                  border: '1px solid #bbf7d0',
                  background: '#f0fdf4',
                  fontSize: 13,
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 600,
                  color: '#166534',
                  textAlign: 'center',
                }}>
                  ✓ Outreach email sent
                </div>
              )
            }
            return (
              <button
                onClick={onOutreach}
                disabled={outreaching}
                style={{
                  padding: '11px',
                  borderRadius: 8,
                  border: '1px solid var(--line)',
                  background: 'var(--surface)',
                  fontSize: 13.5,
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 600,
                  color: 'var(--ink)',
                  cursor: outreaching ? 'not-allowed' : 'pointer',
                  opacity: outreaching ? 0.6 : 1,
                }}
              >
                {outreaching ? 'Sending…' : '📨 Email outreach (claim invite)'}
              </button>
            )
          })()}
          <button
            onClick={onDismiss}
            disabled={dismissing}
            style={{
              padding: '11px',
              borderRadius: 8,
              border: '1px solid #fca5a5',
              background: 'var(--surface)',
              fontSize: 13.5,
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              color: '#b91c1c',
              cursor: dismissing ? 'not-allowed' : 'pointer',
              opacity: dismissing ? 0.6 : 1,
            }}
          >
            {dismissing ? 'Dismissing…' : '✗ Dismiss'}
          </button>
        </div>
      </div>

      {editing && c && (
        <ChurchEditForm church={c} onClose={() => setEditing(false)} />
      )}
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

type FilterTab = 'all' | 'churches' | 'events' | 'flagged' | 'low-confidence' | 'possible-dupes'
type SortKey = 'confidence-asc' | 'confidence-desc' | 'age-asc' | 'age-desc'

export default function AdminReviewQueuePage() {
  const [tab, setTab] = useState<FilterTab>('all')
  const [sort, setSort] = useState<SortKey>('confidence-asc')
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [approvingId, setApprovingId] = useState<string | null>(null)
  const [dismissingId, setDismissingId] = useState<string | null>(null)
  const [outreachingId, setOutreachingId] = useState<string | null>(null)
  const [outreachSentIds, setOutreachSentIds] = useState<Set<string>>(new Set())

  const { data: churchData, isLoading: churchLoading } = useAdminScrapedChurches()
  const { data: eventData, isLoading: eventLoading } = useAdminScrapedEvents()

  const updateChurch = useAdminUpdateChurch()
  const deleteChurch = useAdminDeleteChurch()
  const deleteEvent = useAdminDeleteEvent()
  const churchOutreach = useAdminChurchOutreach()

  const allItems = useMemo(() => {
    const churches = churchData?.data ?? []
    const events = eventData?.data ?? []
    return toQueueItems(churches, events)
  }, [churchData, eventData])

  const filtered = useMemo(() => {
    let items = allItems

    if (tab === 'churches') items = items.filter((i) => i.kind === 'church')
    else if (tab === 'events') items = items.filter((i) => i.kind === 'event')
    else if (tab === 'flagged') items = items.filter((i) => i.risk !== null)
    else if (tab === 'low-confidence') items = items.filter((i) => i.confidence < 3)
    else if (tab === 'possible-dupes') items = items.filter((i) => i.isPossibleDupe)

    if (search.trim()) {
      const q = search.toLowerCase()
      items = items.filter(
        (i) =>
          i.title.toLowerCase().includes(q) ||
          i.location.toLowerCase().includes(q) ||
          (i.source ?? '').toLowerCase().includes(q),
      )
    }

    return [...items].sort((a, b) => {
      if (sort === 'confidence-asc') return a.confidence - b.confidence
      if (sort === 'confidence-desc') return b.confidence - a.confidence
      // age: treat the age string roughly (d > h)
      const ageScore = (age: string) =>
        age.includes('d') ? parseInt(age) * 24 : parseInt(age)
      if (sort === 'age-asc') return ageScore(a.age) - ageScore(b.age)
      return ageScore(b.age) - ageScore(a.age)
    })
  }, [allItems, tab, search, sort])

  const selected = filtered.find((i) => i.id === selectedId) ?? null

  const counts = useMemo(
    () => ({
      all: allItems.length,
      churches: allItems.filter((i) => i.kind === 'church').length,
      events: allItems.filter((i) => i.kind === 'event').length,
      flagged: allItems.filter((i) => i.risk !== null).length,
      lowConf: allItems.filter((i) => i.confidence < 3).length,
      dupes: allItems.filter((i) => i.isPossibleDupe).length,
      autoSafe: allItems.filter((i) => i.kind === 'church' && i.confidence >= 5 && i.risk === null && !i.isPossibleDupe).length,
    }),
    [allItems],
  )

  const isLoading = churchLoading || eventLoading

  function handleApprove(item: QueueItem) {
    if (item.kind !== 'church') return
    setApprovingId(item.id)
    // Approve = confirm data quality. Sets verified:true so the listing is
    // marked reviewed. Does NOT set claimed — that's the church admin's job
    // via the claim flow. Source stays 'scraped' so UI treatment is unchanged.
    updateChurch.mutate(
      { id: item.id, verified: true },
      {
        onSuccess: () => {
          setApprovingId(null)
          if (selectedId === item.id) setSelectedId(null)
        },
        onError: () => setApprovingId(null),
      },
    )
  }

  function handleDismiss(item: QueueItem) {
    setDismissingId(item.id)
    if (item.kind === 'church') {
      deleteChurch.mutate(item.id, {
        onSuccess: () => {
          setDismissingId(null)
          if (selectedId === item.id) setSelectedId(null)
        },
        onError: () => setDismissingId(null),
      })
    } else {
      deleteEvent.mutate(item.id, {
        onSuccess: () => {
          setDismissingId(null)
          if (selectedId === item.id) setSelectedId(null)
        },
        onError: () => setDismissingId(null),
      })
    }
  }

  function handleAutoApprove() {
    const safe = allItems.filter((i) => i.kind === 'church' && i.confidence >= 5 && i.risk === null)
    for (const item of safe) handleApprove(item)
  }

  function handleOutreach(item: QueueItem) {
    if (item.kind !== 'church') return
    const c = item.raw as AdminChurch
    if (!c.email) return
    setOutreachingId(item.id)
    churchOutreach.mutate(item.id, {
      onSuccess: () => {
        setOutreachingId(null)
        setOutreachSentIds((prev) => new Set([...prev, item.id]))
      },
      onError: () => setOutreachingId(null),
    })
  }

  const TABS: { id: FilterTab; label: string; amber?: boolean }[] = [
    { id: 'all', label: `All · ${counts.all}` },
    { id: 'churches', label: `Churches · ${counts.churches}` },
    { id: 'events', label: `Events · ${counts.events}` },
    { id: 'flagged', label: `Flagged · ${counts.flagged}`, amber: true },
    { id: 'low-confidence', label: 'Low confidence' },
    { id: 'possible-dupes', label: counts.dupes > 0 ? `Possible dupes · ${counts.dupes}` : 'Possible dupes', amber: counts.dupes > 0 },
  ]

  return (
    <div>
      {/* Page header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 22,
          gap: 24,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 27,
              letterSpacing: '-0.02em',
              fontWeight: 600,
              margin: '0 0 4px',
              color: 'var(--ink)',
            }}
          >
            Review queue
          </h1>
          <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
            Scraped churches &amp; events awaiting human review
            {allItems.length > 0 && ` · oldest ${allItems[allItems.length - 1]?.age ?? ''}`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button
            style={{
              padding: '9px 14px',
              borderRadius: 8,
              border: '1px solid var(--line)',
              background: 'var(--surface)',
              fontSize: 13,
              fontFamily: 'var(--font-sans)',
              fontWeight: 500,
              cursor: 'pointer',
              color: 'var(--ink)',
            }}
          >
            ⚙ Scraper settings
          </button>
          {counts.autoSafe > 0 && (
            <button
              onClick={handleAutoApprove}
              style={{
                padding: '9px 16px',
                borderRadius: 8,
                border: 'none',
                background: 'linear-gradient(135deg, #3B4CCA, #7C5CD3)',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                fontFamily: 'var(--font-sans)',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(59,76,202,.4)',
              }}
            >
              Auto-approve safe items ({counts.autoSafe})
            </button>
          )}
        </div>
      </div>

      {/* Stat row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: 10,
          marginBottom: 18,
        }}
      >
        {[
          { label: 'In queue', value: String(counts.all), color: 'var(--ink)', sub: `${counts.all} total` },
          { label: 'Churches', value: String(counts.churches), color: '#3B4CCA', sub: 'Unclaimed listings' },
          { label: 'Events', value: String(counts.events), color: '#C75D8A', sub: 'Source: scraped' },
          { label: 'Flagged', value: String(counts.flagged), color: '#b45309', sub: 'Need attention' },
          { label: 'Auto-safe', value: String(counts.autoSafe), color: '#22c55e', sub: 'Conf 5/5 · no risk' },
        ].map((s) => (
          <div
            key={s.label}
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--line-soft)',
              borderRadius: 10,
              padding: '14px 16px',
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: 'var(--muted)',
                fontWeight: 600,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}
            >
              {s.label}
            </div>
            <div
              style={{
                fontSize: 28,
                fontWeight: 600,
                marginTop: 4,
                color: s.color,
                fontFamily: 'ui-monospace,"SF Mono",Menlo,monospace',
                letterSpacing: '-0.02em',
              }}
            >
              {isLoading ? '—' : s.value}
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-2)', marginTop: 2 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--line-soft)',
          borderRadius: 10,
          padding: '9px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          marginBottom: 12,
        }}
      >
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter title, source, location…"
          style={{
            flex: 1,
            border: 'none',
            background: 'transparent',
            outline: 'none',
            fontSize: 13,
            color: 'var(--ink)',
            fontFamily: 'var(--font-sans)',
          }}
        />
        <div style={{ width: 1, height: 20, background: 'var(--line)' }} />
        <div style={{ display: 'flex', gap: 4 }}>
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: '5px 10px',
                borderRadius: 6,
                border: 'none',
                background: tab === t.id ? 'var(--surface-2)' : 'transparent',
                color:
                  tab === t.id
                    ? 'var(--ink)'
                    : t.amber
                      ? '#b45309'
                      : 'var(--ink-2)',
                fontWeight: tab === t.id ? 600 : 500,
                fontSize: 12.5,
                cursor: 'pointer',
                fontFamily: 'var(--font-sans)',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Sort:</span>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            style={{
              padding: '5px 10px',
              borderRadius: 6,
              border: '1px solid var(--line)',
              background: 'var(--surface)',
              fontSize: 12,
              fontFamily: 'var(--font-sans)',
              cursor: 'pointer',
              color: 'var(--ink)',
            }}
          >
            <option value="confidence-asc">Confidence ↑</option>
            <option value="confidence-desc">Confidence ↓</option>
            <option value="age-desc">Oldest first</option>
            <option value="age-asc">Newest first</option>
          </select>
        </div>
      </div>

      {/* Queue table */}
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--line-soft)',
          borderRadius: 12,
          overflow: 'hidden',
        }}
      >
        {/* Table header */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '28px 76px 2.2fr 1.6fr 1fr 0.8fr 1.3fr 200px',
            padding: '9px 16px',
            background: 'var(--surface-2)',
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--muted)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            gap: 10,
            alignItems: 'center',
          }}
        >
          <div />
          <div>Type</div>
          <div>Item</div>
          <div>Source</div>
          <div>Confidence</div>
          <div>Age</div>
          <div>Flags</div>
          <div />
        </div>

        {isLoading && (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--muted)', fontSize: 14 }}>
            Loading scraped items…
          </div>
        )}

        {!isLoading && filtered.length === 0 && (
          <div
            style={{
              padding: '40px 16px',
              textAlign: 'center',
              color: 'var(--muted)',
              fontSize: 14,
            }}
          >
            {allItems.length === 0
              ? 'No scraped items in the queue. Run the scraper to populate this list.'
              : 'No items match the current filter.'}
          </div>
        )}

        {filtered.map((item) => {
          const isSelected = item.id === selectedId
          return (
            <div
              key={item.id}
              onClick={() => setSelectedId(isSelected ? null : item.id)}
              style={{
                display: 'grid',
                gridTemplateColumns: '28px 76px 2.2fr 1.6fr 1fr 0.8fr 1.3fr 200px',
                padding: '13px 16px',
                alignItems: 'center',
                borderTop: '1px solid var(--line-soft)',
                gap: 10,
                background: isSelected ? '#F5F7FF' : 'var(--surface)',
                cursor: 'pointer',
                transition: 'background 0.1s',
              }}
            >
              <div onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => setSelectedId(isSelected ? null : item.id)}
                  style={{ cursor: 'pointer' }}
                />
              </div>

              <div>
                <span
                  style={{
                    fontSize: 10.5,
                    padding: '3px 8px',
                    borderRadius: 5,
                    background: item.kind === 'church' ? '#EEF0FF' : '#F8E6EE',
                    color: item.kind === 'church' ? '#3B4CCA' : '#C75D8A',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  {item.kind}
                </span>
              </div>

              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: 'var(--ink)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.title}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--muted)',
                    marginTop: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.location}
                </div>
              </div>

              <div
                style={{
                  fontSize: 12,
                  fontFamily: 'ui-monospace,"SF Mono",Menlo,monospace',
                  color: '#3B4CCA',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.source ?? 'scraped'}
              </div>

              <ConfBars score={item.confidence} />

              <div
                style={{
                  fontSize: 12,
                  color: 'var(--muted)',
                  fontFamily: 'ui-monospace,"SF Mono",Menlo,monospace',
                }}
              >
                {item.age}
              </div>

              <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {item.risk && (
                  <span style={{ color: '#b45309', fontWeight: 500 }}>
                    <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 999, background: '#f59e0b', marginRight: 5 }} />
                    {item.risk}
                  </span>
                )}
                {item.isPossibleDupe && (
                  <span style={{ color: '#3B4CCA', fontWeight: 500 }}>
                    <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 999, background: '#3B4CCA', marginRight: 5 }} />
                    Possible duplicate
                  </span>
                )}
                {!item.risk && !item.isPossibleDupe && (
                  <span style={{ color: 'var(--muted)' }}>—</span>
                )}
              </div>

              <div
                style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => handleApprove(item)}
                  disabled={item.kind === 'event' || approvingId === item.id}
                  title={item.kind === 'event' ? 'Events are approved via their church' : undefined}
                  style={{
                    padding: '6px 10px',
                    border: 'none',
                    background:
                      item.kind === 'event'
                        ? 'var(--surface-2)'
                        : item.confidence >= 4
                          ? '#22c55e'
                          : 'linear-gradient(135deg, #3B4CCA, #7C5CD3)',
                    color: item.kind === 'event' ? 'var(--muted)' : '#fff',
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: 'var(--font-sans)',
                    cursor: item.kind === 'event' || approvingId === item.id ? 'not-allowed' : 'pointer',
                    opacity: item.kind === 'event' || approvingId === item.id ? 0.5 : 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {approvingId === item.id ? '…' : '✓ Verify'}
                </button>
                <button
                  onClick={() => setSelectedId(item.id)}
                  style={{
                    padding: '6px 10px',
                    border: '1px solid var(--line)',
                    background: 'var(--surface)',
                    borderRadius: 6,
                    fontSize: 12,
                    fontFamily: 'var(--font-sans)',
                    cursor: 'pointer',
                    color: 'var(--ink)',
                  }}
                >
                  Review
                </button>
                <button
                  onClick={() => handleDismiss(item)}
                  disabled={dismissingId === item.id}
                  style={{
                    padding: '6px 10px',
                    border: '1px solid var(--line)',
                    background: 'var(--surface)',
                    borderRadius: 6,
                    fontSize: 12,
                    fontFamily: 'var(--font-sans)',
                    cursor: dismissingId === item.id ? 'not-allowed' : 'pointer',
                    color: '#b91c1c',
                    opacity: dismissingId === item.id ? 0.6 : 1,
                  }}
                >
                  ✗
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {/* Detail panel */}
      {selected && (
        <DetailPanel
          item={selected}
          onApprove={() => handleApprove(selected)}
          onDismiss={() => handleDismiss(selected)}
          onOutreach={() => handleOutreach(selected)}
          approving={approvingId === selected.id}
          dismissing={dismissingId === selected.id}
          outreaching={outreachingId === selected.id}
          outreachSent={outreachSentIds.has(selected.id)}
        />
      )}
    </div>
  )
}
